import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import toast from 'react-hot-toast';
import {
    BreadCrumbs,
    PageTitleWithToolTip,
    AddButton,
    DataGrid,
} from '../Common';
import Routes from '../../routes';
import UserInfo from '../../UserInfo';
import { trakrefFontFamily, postRequestHeaders, fetchFilterValues } from '../../utils';
import { Grid, Chip, Badge, Icon, Tooltip } from '@mui/material';
import useNavigate from '../../hooks/useNavigate';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import StarIcon from '@mui/icons-material/Star';
import { select_commonCellProps, text_commonCellProps } from "../Common/DataGrid/utils";
import { GridActionsCellItem } from "@mui/x-data-grid-pro";
import { Grey2 } from "../../color_utils";

const ManageServiceProviders = ({
    pageTitle,
    toolTipText,
    origin,
}) => {
    const viewingMyServiceProviders = pageTitle === "My Service Providers";
    const [filterOptions, setFilterOptions] = useState([]);
    const [refresh, setRefresh] = useState(false);
    const commonChipStyle = { fontFamily: trakrefFontFamily, fontWeight: 600 };
    const chipIconStyle = { marginLeft: "15px", marginRight: 0 };

    const columns = [
        {
            field: 'baseInstanceName',
            headerName: 'Account',
            ...select_commonCellProps,
        },
        {
            field: 'servicerInstanceId',
            headerName: 'ID',
            ...text_commonCellProps,
        },
        {
            field: 'preferredVendor',
            headerName: 'Preferred Service Provider',
            ...select_commonCellProps,
            initiallyVisible: false,
            renderCell: ({ row: { preferredVendor } }) => {
                return <span>{preferredVendor ? 'Preferred' : 'Not Preferred'}</span>
            }
        },
        {
            field: 'serviceProviderName',
            headerName: 'Service Provider Name',
            ...select_commonCellProps,
            renderCell: ({ row: { preferredVendor, serviceProviderName } }) => {
                if (preferredVendor) {
                    return (
                        <>
                            <Tooltip title="Preferred Service Provider">
                                <Icon sx={{ display: 'inline'}}>
                                    <StarIcon fontSize='small' sx={{ color: "#FDCC0D" }} />
                                </Icon>
                            </Tooltip>
                            {serviceProviderName}
                        </>
                    );
                }
                return serviceProviderName;
            }
        },
        {
            field: 'grantType',
            headerName: 'Grant Type',
            ...select_commonCellProps,
        },
        {
            field: 'canManageLocationsString',
            headerName: 'Can Manage Locations',
            ...select_commonCellProps,
            renderCell: ({ row: { canManageLocations, canManageLocationsString }}) => {
                const { chipColor, chipTextAndBadgeColor } = determineChipStyle(canManageLocations);
                return (
                    <Chip
                        icon={renderBadge(chipTextAndBadgeColor)}
                        label={canManageLocationsString}
                        sx={{
                            bgcolor: chipColor,
                            color: chipTextAndBadgeColor,
                            ...commonChipStyle
                        }}
                    />
                );
            }
        },
        {
            field: 'requireAdminApprovalString',
            headerName: 'Require Admin Approval',
            ...select_commonCellProps,
            renderCell: ({ row: { requireAdminApproval, requireAdminApprovalString }}) => {
                const { chipColor, chipTextAndBadgeColor } = determineChipStyle(requireAdminApproval);
                return (
                    <Chip
                        icon={renderBadge(chipTextAndBadgeColor)}
                        label={requireAdminApprovalString}
                        sx={{
                            bgcolor: chipColor,
                            color: chipTextAndBadgeColor,
                            ...commonChipStyle
                        }}
                    />
                );
            }
        },
        {
            type: 'actions',
            field: 'Quick Actions',
            width: 100,
            getActions: ({ row }) => [
                <GridActionsCellItem
                    icon={
                        <Tooltip
                            title="Edit Service Provider"
                        >
                            <EditIcon sx={{ color: Grey2 }} />
                        </Tooltip>
                    }
                    onClick={() => handleEdit(row)}
                />,
                <GridActionsCellItem
                    icon={
                        <Tooltip
                            title="Delete Service Provider"
                        >
                            <DeleteIcon sx={{ color: Grey2 }} />
                        </Tooltip>
                    }
                    onClick={() => handleDelete(row)}
                />
            ]
        },
    ];

    const navigate = useNavigate({ pageName: 'ManageServiceProviders' });

    const handleCreate = () => navigate.push("LinkServiceProviders");

    const handleEdit = ({
        id,
        enabled,
        servicerInstanceId,
        canManageLocations,
        requireAdminApproval,
        restrictedLocations,
        preferredVendor,
        allowedLocationList
    }) => (
        navigate.push(
            "/LinkServiceProviders",
            null,
            {
                id,
                enabled,
                servicerInstanceId,
                canManageLocations,
                requireAdminApproval,
                restrictedLocations,
                preferredVendor,
                isEditing: true,
                gettingServicerLocations: true,
                viewingManageServiceProviders: !viewingMyServiceProviders,
                allowedLocationList
            }
        )
    );

    const handleDelete = async (row) => {
        const response = await fetch(Routes.Instance.LinkServiceProvider(true), {
            method: 'POST',
            headers: {...postRequestHeaders},
            body: JSON.stringify({ ...row, enabled: false })
        });

        if (response.status === 200) {
            toast.success("The Service Provider has been successfully deleted.", { duration: 6000 });
            return setRefresh(!refresh);
        }

        return toast.error("Could not delete service provider. Please try again", { duration: 6000 });
    };

    const fetchData = async () => {
        let data = await fetchFilterValues(Routes.Instance.ServiceProviderTableFilters());

        console.log({
            data
        })

        setFilterOptions(data);
    };

    const renderBadge = (chipTextAndBadgeColor) => (
        <Badge
            variant="dot"
            sx={{ 
                "&.MuiChip-icon": { ...chipIconStyle },
                "& .MuiBadge-badge": {
                    backgroundColor: chipTextAndBadgeColor,
                    color: chipTextAndBadgeColor,
                }
            }}
        />
    );

    const determineChipStyle = (fieldIsTrue) => {
        if (fieldIsTrue) {
            return {
                chipColor: "#DDFFCD",
                chipTextAndBadgeColor: "#007F00"
            };
        }

        return {
            chipColor: "#FFE6C0",
            chipTextAndBadgeColor:  "#F99500"
        };
    };

    const handleLocationsLinkedToServiceProviders = ({ serviceProviderName }) => {
        const tableURL = Routes.Location.GetServicerLocations();
        const filter = {
            filter: {
                filterModel: {
                    items: [
                        {
                            value: serviceProviderName,
                            field: "servicerName",
                            name: "Service Provider Name",
                            operator: "is",
                        },
                    ],
                },
            }
        };
        localStorage.setItem(`${tableURL}/columns/${UserInfo.instanceId}/${UserInfo.userId}`, JSON.stringify(filter));
        navigate.push("/ServiceProvidersLinkedLocations", "ManageServiceProviders");
    };

    useEffect(() => { fetchData() }, []);

    {/*
        We only want to render this column when the user
        is on the "My Service Provider" screen
    */}
    if (viewingMyServiceProviders) {
        columns.splice(6, 0, {
            field: 'totalAssignedLocations',
            headerName: 'Total Locations',
            sortable: false,
            filterable: false,
            renderCell: ({
                 row,
                 row: { restrictedLocations, totalAssignedLocations }
            }) => {
                if (restrictedLocations) {
                    return (
                        <a
                            onClick={() => handleLocationsLinkedToServiceProviders(row)}
                            style={{ cursor: 'pointer' }}
                        >
                            {totalAssignedLocations}
                        </a>
                    );
                }
                
                return totalAssignedLocations;
            }
        });
    }

    return (
        <>
            <BreadCrumbs
                path={[
                    { text: origin, to: viewingMyServiceProviders ? "/Account" : "/GeekCommandCenter" },
                    { text: pageTitle }
                ]}
            />

            <Grid container spacing={2}>
                <Grid item xs={6}>
                    <PageTitleWithToolTip
                        pageTitle={pageTitle}
                        toolTipText={toolTipText}
                    />
                </Grid>
                {(UserInfo.isSystemAdministrator() &&

                    <Grid item align="right" sx={{ marginLeft: "auto" }}>
                        <AddButton
                            text="Link Service Provider"
                            onClick={handleCreate}
                        />
                    </Grid>

                )}

                <Grid item xs={12} sx={{ marginTop: '1%' }}>
                    <DataGrid
                        url={Routes.Instance.GetServiceProvidersForTable()}
                        exportButtonURL={Routes.Instance.ExportServiceProviderTable()}
                        exportButtonAlt={"Export Work Orders (.csv)"}
                        filterOptions={filterOptions}
                        noResultText="Oops...It is empty here..."
                        columns={columns}
                        refresh={refresh}
                    />
                </Grid>
            </Grid>
        </>
    );
};

ManageServiceProviders.propTypes = {
    pageTitle: PropTypes.string,
    toolTipText: PropTypes.string,
    origin: PropTypes.string,
};

ManageServiceProviders.defaultProps = {
    pageTitle: "Manage Service Providers",
    toolTipText: "Manage which service providers can be linked to the client account, and set up their restrictions and privileges.",
    origin: "Geek Command Center"
};

export default ManageServiceProviders;
